import { Transform, Type } from 'class-transformer';
import {
	ArrayMaxSize,
	ArrayMinSize,
	ArrayUnique,
	IsArray,
	IsBoolean,
	IsDefined,
	IsEnum,
	IsIn,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	IsUUID,
	Length,
	Max,
	MaxLength,
	Min,
	Validate,
	ValidateIf,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';
import { CoursesCourseStatus, CoursesLessonContentType, CoursesQuestionMode } from '../../../enums';

const trim = ({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value);
const trimEach = ({ value }: { value: unknown }) =>
	Array.isArray(value) ? value.map((v) => (typeof v === 'string' ? v.trim() : v)) : value;

export const COURSES_REORDER_MAX_IDS = 500;

export const COURSES_TAG_BULK_MODES = ['add', 'remove', 'replace'] as const;
export type CoursesTagBulkMode = (typeof COURSES_TAG_BULK_MODES)[number];

export const COURSES_ACCESS_OUTCOMES = ['all', 'allowed', 'denied'] as const;
export type CoursesAccessOutcome = (typeof COURSES_ACCESS_OUTCOMES)[number];

export class CoursesAdminUpsertCourseDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@Transform(trim)
	@IsString()
	@Length(1, 200)
	title: string;

	@IsOptional()
	@Transform(trim)
	@IsString()
	@MaxLength(4000)
	description?: string | null;

	@IsBoolean()
	published: boolean;

	@IsOptional()
	@IsEnum(CoursesCourseStatus)
	status?: CoursesCourseStatus;

	@IsOptional()
	@Transform(trimEach)
	@IsArray()
	@IsString({ each: true })
	@Length(1, 80, { each: true })
	tags?: string[];

	@IsOptional()
	@IsBoolean()
	challenges_enabled?: boolean;

	@IsOptional()
	@IsBoolean()
	sequential_quizzes?: boolean;

	@IsOptional()
	@IsBoolean()
	office_hours_bookings_enabled?: boolean;

	@IsOptional()
	@IsBoolean()
	office_hours_webinars_enabled?: boolean;

	@IsOptional()
	@IsInt()
	@Min(0)
	@Max(9999)
	sort_order?: number;
}

export class CoursesAdminReorderCourseModulesDto {
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(COURSES_REORDER_MAX_IDS)
	@ArrayUnique()
	@IsUUID(undefined, { each: true })
	ids: string[];
}

export class CoursesAdminBulkSetCourseTagsDto {
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(200)
	@IsUUID(undefined, { each: true })
	courseIds: string[];

	@Transform(trimEach)
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(50)
	@IsString({ each: true })
	@Length(1, 80, { each: true })
	tags: string[];

	@IsIn(COURSES_TAG_BULK_MODES)
	mode: CoursesTagBulkMode;
}

export class CoursesAdminListCourseTagAuditQueryDto {
	@IsOptional()
	@IsUUID()
	courseId?: string;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(200)
	limit?: number;
}

export class CoursesAdminListAccessDecisionsQueryDto {
	@IsOptional()
	@IsUUID()
	courseId?: string;

	@IsOptional()
	@IsUUID()
	userId?: string;

	@IsOptional()
	@IsIn(COURSES_ACCESS_OUTCOMES)
	outcome?: CoursesAccessOutcome;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(200)
	limit?: number;
}

export class CoursesAdminExplainCourseAccessDto {
	@IsUUID()
	userId: string;

	@IsUUID()
	courseId: string;
}

export class CoursesAdminListAccessUsersQueryDto {
	@IsOptional()
	@Transform(trim)
	@IsString()
	@MaxLength(120)
	search?: string;
}

export class CoursesAdminCreateModuleDto {
	@Transform(trim)
	@IsString()
	@Length(1, 200)
	title: string;

	@IsOptional()
	@Transform(trim)
	@IsString()
	@MaxLength(2000)
	description?: string | null;

	@IsUUID()
	courseId: string;
}

export class CoursesAdminUpdateModuleDto {
	@IsOptional()
	@Transform(trim)
	@IsString()
	@Length(1, 200)
	title?: string;

	@IsOptional()
	@Transform(trim)
	@IsString()
	@MaxLength(2000)
	description?: string | null;

	@IsOptional()
	@IsBoolean()
	locked?: boolean;

	@IsOptional()
	@IsBoolean()
	published?: boolean;

	@IsOptional()
	@IsNumber()
	@Min(0)
	@Max(999)
	estimated_duration_hours?: number | null;

	@IsOptional()
	@IsInt()
	@Min(1)
	number?: number;

	@IsOptional()
	@IsUUID()
	course_id?: string;
}

export class CoursesAdminReorderIdsDto {
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(COURSES_REORDER_MAX_IDS)
	@ArrayUnique()
	@IsUUID(undefined, { each: true })
	ids: string[];
}

export class CoursesAdminCreateLessonDto {
	@Transform(trim)
	@IsString()
	@Length(1, 200)
	title: string;

	@IsEnum(CoursesLessonContentType)
	content_type: CoursesLessonContentType;
}

export class CoursesAdminUpdateLessonDto {
	@IsOptional()
	@Transform(trim)
	@IsString()
	@Length(1, 200)
	title?: string;

	@IsOptional()
	@IsEnum(CoursesLessonContentType)
	content_type?: CoursesLessonContentType;

	@IsOptional()
	content?: unknown;

	@IsOptional()
	@IsInt()
	@Min(0)
	@Max(600)
	duration_minutes?: number | null;

	@IsOptional()
	@IsBoolean()
	published?: boolean;
}

export const COURSES_QUIZ_EXPECTED_ANSWER_REQUIRED =
	'Expected answer is required for Short Answer questions';
export const COURSES_QUIZ_OPTIONS_INVALID =
	'Multiple-choice questions need 2 to 10 options with at least one correct; single-answer questions allow exactly one';
export const COURSES_QUIZ_MAX_QUESTIONS = 100;
export const COURSES_QUIZ_MIN_OPTIONS = 2;
export const COURSES_QUIZ_MAX_OPTIONS = 10;
export const COURSES_QUIZ_EXPECTED_ANSWER_MAX_LENGTH = 500;

/**
 * An expected answer of at most 500 characters, required (non-blank) for short_answer. One
 * constraint so the rule still runs when the value is missing (`@IsOptional` would skip it).
 */
@ValidatorConstraint({ name: 'coursesQuizExpectedAnswer', async: false })
export class CoursesQuizExpectedAnswerConstraint implements ValidatorConstraintInterface {
	validate(value: unknown, args: ValidationArguments): boolean {
		return this.problem(value, args) === null;
	}

	defaultMessage(args: ValidationArguments): string {
		return this.problem(args.value, args) ?? COURSES_QUIZ_EXPECTED_ANSWER_REQUIRED;
	}

	private problem(value: unknown, args: ValidationArguments): string | null {
		if (value !== null && value !== undefined) {
			if (typeof value !== 'string') {
				return `${args.property} must be a string`;
			}
			if (value.length > COURSES_QUIZ_EXPECTED_ANSWER_MAX_LENGTH) {
				return `${args.property} must be shorter than or equal to ${COURSES_QUIZ_EXPECTED_ANSWER_MAX_LENGTH} characters`;
			}
		}
		const mode = (args.object as { mode?: unknown }).mode;
		if (mode === CoursesQuestionMode.ShortAnswer && !(typeof value === 'string' && value.trim())) {
			return COURSES_QUIZ_EXPECTED_ANSWER_REQUIRED;
		}

		return null;
	}
}

@ValidatorConstraint({ name: 'coursesQuizOptions', async: false })
export class CoursesQuizOptionsConstraint implements ValidatorConstraintInterface {
	validate(value: unknown, args: ValidationArguments): boolean {
		const question = args.object as { mode?: unknown; allow_multiple_correct?: unknown };
		if (question.mode !== CoursesQuestionMode.FixedAnswer) {
			return true;
		}
		if (
			!Array.isArray(value) ||
			value.length < COURSES_QUIZ_MIN_OPTIONS ||
			value.length > COURSES_QUIZ_MAX_OPTIONS
		) {
			return false;
		}
		const correct = value.filter(
			(option) => (option as { is_correct?: unknown })?.is_correct === true
		).length;

		return question.allow_multiple_correct === true ? correct >= 1 : correct === 1;
	}

	defaultMessage(): string {
		return COURSES_QUIZ_OPTIONS_INVALID;
	}
}

export class CoursesAdminQuizOptionInputDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@Transform(trim)
	@IsString()
	@Length(1, 500)
	text: string;

	@IsBoolean()
	is_correct: boolean;
}

export class CoursesAdminQuizQuestionInputDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@Transform(trim)
	@IsString()
	@Length(1, 1000)
	text: string;

	@IsEnum(CoursesQuestionMode)
	mode: CoursesQuestionMode;

	@IsBoolean()
	allow_multiple_correct: boolean;

	@Transform(trim)
	@Validate(CoursesQuizExpectedAnswerConstraint)
	expected_answer?: string | null;

	@ValidateIf(
		(o: CoursesAdminQuizQuestionInputDto) =>
			o.mode === CoursesQuestionMode.FixedAnswer || (o.options !== undefined && o.options !== null)
	)
	@Validate(CoursesQuizOptionsConstraint)
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => CoursesAdminQuizOptionInputDto)
	options?: CoursesAdminQuizOptionInputDto[];
}

export class CoursesAdminQuizSettingsInputDto {
	@ValidateIf((o: CoursesAdminQuizSettingsInputDto) => o.intro_text !== null)
	@IsString()
	@MaxLength(2000)
	intro_text: string | null;

	@IsBoolean()
	remove_passing_grade: boolean;

	@ValidateIf((o: CoursesAdminQuizSettingsInputDto) => o.retakes_allowed !== null)
	@IsInt()
	@Min(0)
	@Max(20)
	retakes_allowed: number | null;
}

export class CoursesAdminSaveQuizDto {
	@IsDefined()
	@ValidateNested()
	@Type(() => CoursesAdminQuizSettingsInputDto)
	settings: CoursesAdminQuizSettingsInputDto;

	@IsArray()
	@ArrayMaxSize(COURSES_QUIZ_MAX_QUESTIONS)
	@ValidateNested({ each: true })
	@Type(() => CoursesAdminQuizQuestionInputDto)
	questions: CoursesAdminQuizQuestionInputDto[];
}
